/**
 * AthleteTabs — sticky horizontal tabs that let a parent switch which kid
 * the dashboard is showing. Only rendered when the parent account has 2+
 * athletes; for single-kid families the active athlete is auto-resolved and
 * no UI is needed. The selection is persisted per-user in localStorage via
 * AuthContext.setActiveAthleteId.
 *
 * Maxes, workout sessions, and weight intents on every athlete-side endpoint
 * are scoped by the selected athlete_id, so switching tabs immediately re-
 * routes everything to that kid's profile — without that kid needing their
 * own login.
 */
import { useAuth } from "@/context/AuthContext";
import { User } from "lucide-react";

export default function AthleteTabs() {
    const { user, activeAthleteId, setActiveAthleteId } = useAuth();
    if (!user || user.athlete_mode_for) return null;
    const athletes = user.athletes || [];
    if (athletes.length < 2) return null;

    return (
        <div
            data-testid="athlete-tabs"
            className="border-b border-zinc-900 bg-black/90 backdrop-blur sticky top-20 z-30"
        >
            <div className="max-w-6xl mx-auto px-6 md:px-10 py-3 flex items-center gap-2 overflow-x-auto">
                <span className="text-[10px] tracking-[0.25em] uppercase text-zinc-500 flex-shrink-0 mr-2">
                    Athlete
                </span>
                {athletes.map((a) => {
                    const active = a.athlete_id === activeAthleteId;
                    const name = `${a.first_name || ""} ${(a.last_name || "")[0] || ""}`.trim() || "Athlete";
                    return (
                        <button
                            key={a.athlete_id}
                            type="button"
                            data-testid={`athlete-tab-${a.athlete_id}`}
                            onClick={() => setActiveAthleteId(a.athlete_id)}
                            className={`flex-shrink-0 inline-flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border transition-colors ${
                                active
                                    ? "bg-brand-red text-white border-brand-red"
                                    : "bg-transparent text-zinc-300 border-zinc-800 hover:border-brand-red hover:text-white"
                            }`}
                        >
                            <User className="w-3.5 h-3.5" />
                            {name}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
